/**
 * 사용자 목록 테이블 컴포넌트
 * - 검색 및 필터링
 * - 페이지네이션
 * - 역할별 액션 버튼
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Dropdown,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  PlusOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { userService, UserListParams } from '../../services/userService';
import type { User } from '../../types/user';
import { getRoleLabel, getRoleColor } from '../layout/menuConfig';
import { useAuthStore } from '../../store/authStore';

const { Search } = Input;
const { Option } = Select;

/**
 * 테이블 필터 상태
 */
interface FilterState {
  search: string;
  role?: string;
  is_active?: boolean;
}

interface UserTableProps {
  onCreateInstructor: () => void;
  onCreateStudent: () => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onAssignStudents: (instructor: User) => void;
  onChangeInstructor: (student: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  onCreateInstructor,
  onCreateStudent,
  onEdit,
  onDelete,
  onAssignStudents,
  onChangeInstructor,
}) => {
  const { user: currentUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
  });

  /**
   * 사용자 목록 조회
   */
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params: UserListParams = {
        page,
        page_size: pageSize,
        search: filters.search || undefined,
        role: filters.role,
        is_active: filters.is_active,
      };

      // 그룹 관리자는 자신의 그룹만 조회
      if (currentUser?.role === 'group_admin' && currentUser?.group_id) {
        params.group_id = currentUser.group_id;
      }

      const response = await userService.getUsers(params);
      setUsers(response.users);
      setTotal(response.total);
    } catch (error) {
      message.error('사용자 목록 조회 실패');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 초기 로드 및 필터/페이지 변경 시 재조회
   */
  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, filters]);

  /**
   * 검색 처리
   */
  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(1); // 검색 시 첫 페이지로 이동
  };

  /**
   * 역할 필터 변경
   */
  const handleRoleFilter = (value: string | undefined) => {
    setFilters((prev) => ({ ...prev, role: value }));
    setPage(1);
  };

  /**
   * 활성 상태 필터 변경
   */
  const handleActiveFilter = (value: boolean | undefined) => {
    setFilters((prev) => ({ ...prev, is_active: value }));
    setPage(1);
  };

  /**
   * 페이지 변경 처리
   */
  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };

  /**
   * 액션 메뉴 아이템 생성
   */
  const getActionMenuItems = (record: User): MenuProps['items'] => {
    const items: MenuProps['items'] = [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: '수정',
        onClick: () => onEdit(record),
      },
    ];

    // 강사인 경우 학생 할당 메뉴 추가
    if (record.role === 'instructor') {
      items.push({
        key: 'assign',
        icon: <UserAddOutlined />,
        label: '학생 할당',
        onClick: () => onAssignStudents(record),
      });
    }

    // 학생인 경우 담당 강사 변경 메뉴 추가
    if (record.role === 'student') {
      items.push({
        key: 'changeInstructor',
        icon: <UserAddOutlined />,
        label: '담당 강사 변경',
        onClick: () => onChangeInstructor(record),
      });
    }

    // 자기 자신이 아닌 경우에만 삭제 가능
    if (record.id !== currentUser?.id) {
      items.push({
        type: 'divider',
      });
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: '삭제',
        danger: true,
        onClick: () => onDelete(record),
      });
    }

    return items;
  };

  /**
   * 테이블 컬럼 정의
   */
  const columns: ColumnsType<User> = [
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '이메일',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '전화번호',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (phone) => phone || '-',
    },
    {
      title: '역할',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role) => (
        <Tag color={getRoleColor(role)}>{getRoleLabel(role)}</Tag>
      ),
    },
    {
      title: '소속 그룹',
      dataIndex: 'group_name',
      key: 'group_name',
      width: 150,
      render: (groupName) => groupName || '-',
    },
    {
      title: '담당 강사',
      dataIndex: 'instructor_names',
      key: 'instructor_names',
      width: 150,
      render: (instructorNames: string[], record) => {
        if (record.role !== 'student') return '-';
        if (!instructorNames || instructorNames.length === 0) {
          return <Tag color="default">미배정</Tag>;
        }
        return (
          <Space size={4} wrap>
            {instructorNames.map((name, index) => (
              <Tag key={index} color="blue">{name}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '활성' : '비활성'}
        </Tag>
      ),
    },
    {
      title: '생성일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('ko-KR'),
    },
    {
      title: '액션',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      {/* 검색 및 필터 영역 */}
      <Space
        style={{
          marginBottom: 16,
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Space>
          <Search
            placeholder="이름 또는 이메일 검색"
            allowClear
            onSearch={handleSearch}
            style={{ width: 250 }}
          />
          <Select
            placeholder="역할 필터"
            allowClear
            style={{ width: 120 }}
            onChange={handleRoleFilter}
          >
            <Option value="admin">시스템 관리자</Option>
            <Option value="group_admin">그룹 관리자</Option>
            <Option value="instructor">강사</Option>
            <Option value="student">학생</Option>
          </Select>
          <Select
            placeholder="상태 필터"
            allowClear
            style={{ width: 120 }}
            onChange={handleActiveFilter}
          >
            <Option value={true}>활성</Option>
            <Option value={false}>비활성</Option>
          </Select>
        </Space>

        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreateInstructor}
          >
            강사 추가
          </Button>
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={onCreateStudent}
          >
            학생 추가
          </Button>
        </Space>
      </Space>

      {/* 테이블 */}
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => `총 ${total}명`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};
